import React, { useEffect, useState } from "react";
import axios from "axios";
import { logHistory } from "../../utils/historyLogger";

function PolicyMatters() {
  const [policies, setPolicies] = useState([]);
  const role = localStorage.getItem("role");
  const canEdit = role === "admin" || role === "superadmin";
  const canDelete = role === "superadmin";
  // Any logged-in role (user, admin, superadmin) can add data now.
  const canAdd = role === "user" || role === "admin" || role === "superadmin";
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState({
    date: "",
    reason: "",
    nic: "",
    epfNo: "",
    employeeName: "",
    department: "",
    remark: "",
    status: "Pending",
  });

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      const res = await axios.get("https://hrpsc.swisstekaluminium.com/api/policies");
      setPolicies(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.log(err);
      alert("Failed to fetch policies");
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const clearForm = () => {
    setFormData({
      date: "",
      reason: "",
      nic: "",
      epfNo: "",
      employeeName: "",
      department: "",
      remark: "",
      status: "Pending",
    });

    setEditingId(null);
  };

  const handleSubmit = async () => {
    if (!formData.nic || !formData.employeeName || !formData.reason) {
      alert("NIC, Employee Name and Reason are required");
      return;
    }

    try {
      const payload = {
        date: formData.date,
        reason: formData.reason,
        nic: formData.nic,
        epfNo: formData.epfNo,
        employeeName: formData.employeeName,
        department: formData.department,
        remark: formData.remark,
        status: formData.status || "Pending",
      };

      if (editingId) {
        await axios.put(
          `https://hrpsc.swisstekaluminium.com/api/policies/${editingId}`,
          payload
        );

        await logHistory(
          "Policy Matters",
          "Updated Policy",
          "Policy updated"
        );

        alert("Updated Successfully");
      } else {
        await axios.post("https://hrpsc.swisstekaluminium.com/api/policies", payload);

        await logHistory(
          "Policy Matters",
          "Added Policy",
          "Policy added"
        );

        alert("Policy Added Successfully");
      }

      fetchPolicies();
      clearForm();
    } catch (err) {
      console.log(err);
      alert(err.response?.data?.message || "Error Saving Policy");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this policy?")) return;

    try {
      await axios.delete(`https://hrpsc.swisstekaluminium.com/api/policies/${id}`);

      await logHistory(
        "Policy Matters",
        "Deleted Policy",
        "Policy deleted"
      );

      fetchPolicies();
      alert("Deleted Successfully");
    } catch (err) {
      console.log(err);
      alert("Delete failed");
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);

    setFormData({
      date: item.date ? item.date.substring(0, 10) : "",
      reason: item.reason || "",
      nic: item.nic || "",
      epfNo: item.epfNo || "",
      employeeName: item.employeeName || "",
      department: item.department || "",
      remark: item.remark || "",
      status: item.status || "Pending",
    });

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const toggleStatus = async (id, currentStatus) => {
    try {
      const updatedStatus =
        currentStatus === "Pending" ? "Completed" : "Pending";

      const selectedPolicy = policies.find((item) => item.id === id);

      if (!selectedPolicy) {
        alert("Policy not found");
        return;
      }

      await axios.put(`https://hrpsc.swisstekaluminium.com/api/policies/${id}`, {
        date: selectedPolicy.date,
        reason: selectedPolicy.reason,
        nic: selectedPolicy.nic,
        epfNo: selectedPolicy.epfNo,
        employeeName: selectedPolicy.employeeName,
        department: selectedPolicy.department,
        remark: selectedPolicy.remark,
        status: updatedStatus,
      });

      await logHistory(
        "Policy Matters",
        "Updated Policy Status",
        `Policy status changed to ${updatedStatus}`
      );

      fetchPolicies();
    } catch (err) {
      console.log(err);
      alert("Status update failed");
    }
  };

  const downloadCSVReport = () => {
    if (policies.length === 0) {
      alert("No policy data to download");
      return;
    }

    const headers = [
      "date",
      "reason",
      "nic",
      "epfNo",
      "employeeName",
      "department",
      "remark",
      "status",
    ];

    const rows = policies.map((item) =>
      headers
        .map((field) => `"${String(item[field] ?? "").replace(/"/g, '""')}"`)
        .join(",")
    );

    const csvContent = [headers.join(","), ...rows].join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "policy_matters_report.csv";
    link.click();

    URL.revokeObjectURL(url);
  };

  const filteredPolicies = policies.filter((item) => {
    const text = search.toLowerCase();

    return (
      item.employeeName?.toLowerCase().includes(text) ||
      item.nic?.toLowerCase().includes(text) ||
      item.epfNo?.toLowerCase().includes(text)
    );
  });

  return (
    <div className="page-container">
      <h1 className="page-title">Policy Matters</h1>

      <div className="cards">
        <div className="card">
          <h3>Total Cases</h3>
          <p>{policies.length}</p>
        </div>

        <div className="card">
          <h3>Completed</h3>
          <p>{policies.filter((item) => item.status === "Completed").length}</p>
        </div>

        <div className="card">
          <h3>Pending</h3>
          <p>{policies.filter((item) => item.status === "Pending").length}</p>
        </div>
      </div>

      <div className="search-section">
        <input
          type="text"
          className="search-input"
          placeholder="Search Name / NIC / EPF"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <button className="clear-btn" onClick={() => setSearch("")}>
          Clear
        </button>

        <button className="primary-btn" onClick={downloadCSVReport}>
          Download Report
        </button>
      </div>

      {(canAdd || editingId) && (
        <>
          <div className="form-grid">
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
            />

            <input
              type="text"
              name="reason"
              placeholder="Reason"
              value={formData.reason}
              onChange={handleChange}
            />

            <input
              type="text"
              name="nic"
              placeholder="NIC *"
              value={formData.nic}
              onChange={handleChange}
            />

            <input
              type="text"
              name="epfNo"
              placeholder="EPF No"
              value={formData.epfNo}
              onChange={handleChange}
            />

            <input
              type="text"
              name="employeeName"
              placeholder="Employee Name *"
              value={formData.employeeName}
              onChange={handleChange}
            />

            <input
              type="text"
              name="department"
              placeholder="Department"
              value={formData.department}
              onChange={handleChange}
            />

            <input
              type="text"
              name="remark"
              placeholder="Remark"
              value={formData.remark}
              onChange={handleChange}
            />
          </div>

          <div className="button-group">
            <button className="primary-btn" onClick={handleSubmit}>
              {editingId ? "Update Policy" : "Add Policy"}
            </button>

            <button className="clear-btn" onClick={clearForm}>
              Clear Form
            </button>
          </div>
        </>
      )}

      <div className="table-container">
        <table className="custom-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Reason</th>
              <th>NIC</th>
              <th>EPF</th>
              <th>Name</th>
              <th>Department</th>
              <th>Remark</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {filteredPolicies.length > 0 ? (
              filteredPolicies.map((item) => (
                <tr key={item.id}>
                  <td>{item.date}</td>
                  <td>{item.reason}</td>
                  <td>{item.nic}</td>
                  <td>{item.epfNo}</td>
                  <td>{item.employeeName}</td>
                  <td>{item.department}</td>
                  <td>{item.remark}</td>

                  <td>
                    {canEdit ? (
                      <button
                        className={
                          item.status === "Completed"
                            ? "completed-status"
                            : "pending-status"
                        }
                        onClick={() => toggleStatus(item.id, item.status)}
                      >
                        {item.status || "Pending"}
                      </button>
                    ) : (
                      <span
                        className={
                          item.status === "Completed"
                            ? "completed-status"
                            : "pending-status"
                        }
                      >
                        {item.status || "Pending"}
                      </span>
                    )}
                  </td>

                  <td>
                    <div className="action-buttons">
                      {canEdit && (
                        <button
                          className="edit-btn"
                          onClick={() => handleEdit(item)}
                        >
                          Edit
                        </button>
                      )}

                      {canDelete && (
                        <button
                          className="delete-btn"
                          onClick={() => handleDelete(item.id)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="9"
                  style={{
                    textAlign: "center",
                    padding: "30px",
                    color: "gray",
                  }}
                >
                  No Policy Records Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PolicyMatters;